import {
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';

interface MetricCardProps {
  label: string;
  value: string | number;
  supportingText?: string;
  secondaryValue?: string | number;
  secondaryLabel?: string;
  description?: string;
  className?: string;
  valueClassName?: string;
  onClick?: () => void;
  isLoading?: boolean;
}

export function MetricCard({
  label,
  value,
  supportingText,
  secondaryValue,
  secondaryLabel = 'all time',
  description,
  className = '',
  valueClassName = '',
  onClick,
  isLoading = false,
}: MetricCardProps) {
  const content = (
    <Card
      className={`h-full min-h-[190px] border border-gray-200 shadow-sm ${onClick ? 'cursor-pointer transition-shadow hover:shadow-md' : ''} ${className}`}
      onClick={onClick}
      aria-busy={isLoading}
    >
      <CardContent className="p-8">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-base font-medium text-gray-600">{label}</p>
            {isLoading ? (
              <div data-testid={`${label}-loading`}>
                <Skeleton className="mt-4 h-10 w-24" />
                {supportingText && <Skeleton className="mt-4 h-5 w-40" />}
                {secondaryValue !== undefined && <Skeleton className="mt-2 h-4 w-24" />}
              </div>
            ) : (
              <>
                <p className={`mt-4 text-4xl font-bold text-gray-900 ${valueClassName}`}>{value}</p>
                {supportingText && <p className="mt-4 text-base text-gray-700">{supportingText}</p>}
                {secondaryValue !== undefined && (
                  <p className="mt-1 text-xs text-gray-700">
                    {secondaryValue} {secondaryLabel}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (description) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="h-full cursor-help">{content}</div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs">{description}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}
